<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import AutoComplete, { type AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Textarea from 'primevue/textarea';
import { useToast } from 'primevue/usetoast';
import { idNameProblem, normalizeIdName } from '@/lib/idName';
import { useMetadataStore } from '@/stores/metadata';
import { useServicesStore } from '@/stores/services';

// Standalone "Add service" dialog. Scaffolds services/{name}/_meta.json, _base.json, and
// one {env}.json per registered environment (server side), then writes optional metadata.
//
// Used by App.vue for the Monaco "Create service" quick-fix (prefilled, no navigation so
// the operator isn't yanked out of the config file they were editing). `navigateOnCreate`
// lets a caller (e.g. a future ServicesView consolidation) opt into the post-create jump.
//
// Self-contained on purpose — it does not reach into ServicesView. Keeping it additive
// avoids a blind refactor of that 500-line view; consolidating ServicesView onto this
// component is a deliberate follow-up that wants a build to verify.
const props = defineProps<{
  visible: boolean;
  prefillName?: string;
  navigateOnCreate?: boolean;
}>();
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'created', name: string): void;
}>();

const store = useServicesStore();
const meta = useMetadataStore();
const toast = useToast();
const router = useRouter();
const { list } = storeToRefs(store);

// Folder-backed → lowercase canonical rule; "_base"/"_vars" reserved (layer-file basenames).
const RESERVED = new Set(['_base', '_vars']);

const name = ref('');
const displayName = ref('');
const category = ref('');
const description = ref('');
const busy = ref(false);
const formError = ref<string | null>(null);

const categoryOptions = ref<string[]>([]);
const existingCategories = computed(() =>
  Array.from(
    new Set(
      list.value
        .map((s) => s.category)
        .filter((c): c is string => !!c && c.trim().length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b)));

function onCategorySearch(event: AutoCompleteCompleteEvent) {
  const all = existingCategories.value;
  const term = (event.query ?? '').trim().toLowerCase();
  categoryOptions.value = term ? all.filter((c) => c.toLowerCase().includes(term)) : all.slice();
}

watch(
  () => props.visible,
  (v) => {
    if (v) {
      name.value = props.prefillName ? normalizeIdName(props.prefillName) : '';
      displayName.value = '';
      category.value = '';
      description.value = '';
      formError.value = null;
      busy.value = false;
    }
  },
  { immediate: true },
);

const nameFormatError = computed(() => idNameProblem(name.value, { reserved: RESERVED }));
const nameDuplicateError = computed<string | null>(() => {
  const trimmed = name.value.trim();
  if (trimmed === '' || nameFormatError.value !== null) return null;
  const exists = list.value.some((s) => s.name.toLowerCase() === trimmed.toLowerCase());
  return exists ? `A service named '${trimmed}' already exists.` : null;
});
const nameError = computed(() => nameFormatError.value ?? nameDuplicateError.value);
const canSubmit = computed(
  () => !busy.value && name.value.trim() !== '' && nameError.value === null,
);

function close() {
  if (busy.value) return;
  emit('update:visible', false);
}

async function submit() {
  if (!canSubmit.value) return;
  const finalName = normalizeIdName(name.value, { trimTrailingDash: true });
  busy.value = true;
  formError.value = null;

  const addResult = await store.add(finalName);
  if (!addResult.ok) {
    busy.value = false;
    formError.value = addResult.error;
    return;
  }

  const dn = displayName.value.trim();
  const desc = description.value;
  const cat = category.value.trim();
  if (dn || desc.length > 0 || cat) {
    const metaResult = await meta.updateService(finalName, {
      displayName: dn || null,
      description: desc.length > 0 ? desc : null,
      category: cat || null,
    });
    if (!metaResult.ok) {
      busy.value = false;
      formError.value = `Service created, but metadata save failed: ${metaResult.error}`;
      return; // keep dialog open so the partial failure is visible
    }
  }

  busy.value = false;
  toast.add({ severity: 'success', summary: 'Service created', detail: finalName, life: 3000 });
  emit('created', finalName);
  emit('update:visible', false);
  if (props.navigateOnCreate) {
    router.push(`/services/${encodeURIComponent(finalName)}`);
  }
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!busy"
    :close-on-escape="!busy"
    :style="{ width: '560px' }"
    @update:visible="(v: boolean) => { if (!v) close(); }"
  >
    <template #header><span>Add service</span></template>
    <p class="hint">
      Scaffolds <code class="mono">services/{name}/_meta.json</code>,
      <code class="mono">_base.json</code>, and one <code class="mono">{env}.json</code> per
      registered environment. Display name, category, and description are optional and can
      be edited later.
    </p>
    <div class="field-stack">
      <label class="field-label" for="new-service-name">Name</label>
      <InputText
        id="new-service-name"
        :model-value="name"
        placeholder="service-name (e.g. azure-blob)"
        class="mono"
        fluid
        :disabled="busy"
        :invalid="nameError !== null"
        @update:model-value="(v: string | undefined) => { name = normalizeIdName(v ?? ''); }"
        @keyup.enter="submit"
      />
      <p v-if="nameError" class="inline-error">{{ nameError }}</p>

      <label class="field-label" for="new-service-display">Display name <span class="optional">(optional)</span></label>
      <InputText
        id="new-service-display"
        v-model="displayName"
        placeholder="Human-friendly name (e.g. Azure Blob Storage)"
        fluid
        :disabled="busy"
      />

      <label class="field-label" for="new-service-category">Category <span class="optional">(optional)</span></label>
      <AutoComplete
        id="new-service-category"
        v-model="category"
        :suggestions="categoryOptions"
        :disabled="busy"
        dropdown
        placeholder="e.g. APIs, Databases — type to mint a new bucket"
        fluid
        @complete="onCategorySearch"
      />

      <label class="field-label" for="new-service-description">Description <span class="optional">(optional, markdown supported)</span></label>
      <Textarea
        id="new-service-description"
        v-model="description"
        rows="4"
        placeholder="Short description — operators can flesh this out later via the pencil-edit dialog."
        :disabled="busy"
        auto-resize
        fluid
      />
    </div>
    <Message v-if="formError" severity="error" :closable="false" class="form-error">{{ formError }}</Message>
    <template #footer>
      <Button label="Cancel" severity="secondary" :disabled="busy" @click="close" />
      <Button label="Create" icon="pi pi-plus" :loading="busy" :disabled="!canSubmit" @click="submit" />
    </template>
  </Dialog>
</template>

<style scoped>
.hint { color: var(--retail-hint-text); font-size: 0.8125rem; margin: 0 0 1rem 0; line-height: 1.5; }
.hint code { font-family: 'Cascadia Code', Consolas, monospace; background: var(--retail-code-bg); padding: 0.05rem 0.3rem; border-radius: 3px; }
.field-stack { display: flex; flex-direction: column; gap: 0.4rem; }
.field-label { font-weight: 600; font-size: 0.875rem; margin-top: 0.5rem; }
.field-label .optional { font-weight: 400; color: var(--retail-hint-text); }
.inline-error { color: var(--retail-error-text); font-size: 0.8125rem; margin: 0.1rem 0 0 0; }
.form-error { margin-top: 0.75rem; }
.mono { font-family: 'Cascadia Code', Consolas, monospace; }
</style>
